import Honeybadger from 'honeybadger-js';
import React, { useMemo } from 'react';

import { useSendCovidSmsMutation } from '../../../generated/graphql';
import { getChangedValues } from '../../../helpers/getChangedValues';
import { splitHouseholds } from '../../../helpers/households';
import { editCovidSmsPath, editCovidSmsRoute } from '../../../helpers/routes';
import SmsSequence, { ShareValues } from '../../SharePage/SmsSequence';
import useShareTracking from '../../SharePage/useShareTracking';
import { useFilteredHouseholds } from '../GuestFilters/useFilteredHouseholds';
import { useSequenceState } from './useSequenceState';

const CovidSMS: React.FC = () => {
  const [sendSms, { loading: sendingMessage }] = useSendCovidSmsMutation();
  const {
    sequence,
    wwsLoading,
    guestIds,
    setGuestIds,
    setHideRecaptcha,
    hideRecaptcha,
    dispatch,
    recaptchaRef,
    seqId,
    showError,
    errorMessage,
    showSuccess,
    householdLoading,
    greeting,
    defaultMessage,
    optout,
  } = useSequenceState();

  const { filteredHouseholds, setFilterCriteria } = useFilteredHouseholds();
  const households = useMemo(() => splitHouseholds('phone', filteredHouseholds), [filteredHouseholds]);
  const track = useShareTracking();

  let initialValues: ShareValues = {
    greeting,
    message: defaultMessage,
    optout,
  };
  const sendMessage = async (values: ShareValues) => {
    if (wwsLoading || sendingMessage) {
      return null;
    }

    const edited = getChangedValues(initialValues, values);

    track('Website Shared', {
      guestMessagedCount: guestIds.length,
      message: values.message,
      messageCustomized: Boolean(Object.keys(edited).length),
      messageType: sequence?.messagingSequence?.displayName,
      medium: 'sms',
    });

    dispatch({ type: 'RESET', payload: { hideRecaptcha: true, showSuccess: false, showError: false } });

    try {
      await sendSms({
        variables: {
          args: {
            body: '',
            guestIds,
            token: recaptchaRef.current?.getValue() || '',
          },
        },
      });
      dispatch({ type: 'SUCCESS' });
    } catch (error) {
      Honeybadger.notify(error, 'Error sending covid sms', { context: { error } });
      dispatch({ type: 'ERROR', payload: { message: error.message } });
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  return (
    <SmsSequence
      onSend={sendMessage}
      loading={sendingMessage}
      setGuestIds={setGuestIds}
      showError={showError}
      errorMessage={errorMessage}
      showSuccess={showSuccess}
      initialValues={initialValues}
      hideRecaptcha={hideRecaptcha}
      setHideRecaptcha={setHideRecaptcha}
      recaptchaRef={recaptchaRef}
      editPath={editCovidSmsPath}
      editRoute={editCovidSmsRoute(seqId)}
      households={households}
      householdLoading={householdLoading}
      wwsLoading={wwsLoading}
      displayName={sequence?.messagingSequence?.displayName}
      setFilter={setFilterCriteria}
    />
  );
};

export default CovidSMS;
